/**
 * CSV Export Utilities
 *
 * Functions for converting data to CSV format and downloading files
 * Matches Angular's CsvService pattern
 */

/**
 * Convert 2D array to CSV string
 */
function arrayToCSV(data: (string | number)[][]): string {
  return data
    .map((row) =>
      row
        .map((cell) => {
          const cellStr = cell?.toString() || '';
          // Escape quotes and wrap in quotes if contains comma, newline, or quote
          if (cellStr.includes(',') || cellStr.includes('\n') || cellStr.includes('"')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        })
        .join(',')
    )
    .join('\n');
}

/**
 * Trigger file download in browser
 */
function downloadFile(content: string, filename: string, mimeType = 'text/csv;charset=utf-8;') {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * CSV Service - matches Angular CsvService pattern
 */
export class CSVService {
  /**
   * Convert data rows to CSV with headers
   */
  static convert(rows: (string | number)[][], headers?: (string | number)[]) {
    const data = headers ? [headers, ...rows] : rows;
    const csv = arrayToCSV(data);

    return {
      download: (filename: string) => {
        const fullFilename = filename.endsWith('.csv') ? filename : `${filename}.csv`;
        downloadFile(csv, fullFilename);
      },
      toString: () => csv,
    };
  }

  /**
   * Export data array directly to CSV
   */
  static export(data: (string | number)[][], filename: string, headers?: (string | number)[]) {
    const csv = this.convert(data, headers);
    csv.download(filename);
  }
}

/**
 * Create spacer row for CSV (empty row for visual separation)
 * Matches Angular pattern: const spacer = new Array(columns).fill('')
 */
export function createSpacerRow(columnCount: number): string[] {
  return new Array(columnCount).fill('');
}

/**
 * Download blob file (for Excel/PDF downloads)
 */
export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Extract filename from Content-Disposition header
 */
export function extractFilenameFromHeader(contentDisposition: string | null): string | null {
  if (!contentDisposition) return null;

  const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  if (filenameMatch && filenameMatch[1]) {
    return filenameMatch[1].replace(/['"]/g, '');
  }

  return null;
}
